import React from 'react';

// A beautiful, fully responsive bottom-sheet / centered modal that explains how
// to install UttaApp manually. Shown on iOS (where the native prompt is not
// available) or whenever the native prompt cannot be triggered.
const PWAInstallInstructions = ({ isIOS = false, onClose }) => {
  const steps = isIOS
    ? [
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16V4m0 0L8 8m4-4l4 4M20 16v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2" />
            </svg>
          ),
          title: 'Tap the Share button',
          desc: 'Open the Share menu at the bottom of Safari.',
        },
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          ),
          title: 'Add to Home Screen',
          desc: 'Scroll and choose "Add to Home Screen".',
        },
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          title: 'Open UttaApp',
          desc: 'Tap "Add", then launch UttaApp from your home screen.',
        },
      ]
    : [
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          ),
          title: 'Open the browser menu',
          desc: 'Tap the menu (⋮) in the top-right corner.',
        },
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          ),
          title: 'Install app',
          desc: 'Choose "Install app" or "Add to Home screen".',
        },
        {
          icon: (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          title: 'Open UttaApp',
          desc: 'Confirm, then launch UttaApp from your apps.',
        },
      ];

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center animate-pwa-fade"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="How to install UttaApp"
    >
      <div
        className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-2xl animate-pwa-slide-up sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1.5 w-12 rounded-full bg-slate-200 sm:hidden" />

        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-[#0f766e]/10 p-1.5">
            <img src="/icon-192.png" alt="UttaApp" className="h-full w-full rounded-xl object-cover" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Install UttaApp</h3>
            <p className="text-sm text-slate-500">Add to your home screen in 3 steps</p>
          </div>
        </div>

        <ol className="space-y-3">
          {steps.map((step, index) => (
            <li key={step.title} className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0f766e] text-sm font-bold text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  <span className="text-[#0f766e]">{step.icon}</span>
                  {step.title}
                </div>
                <p className="mt-0.5 text-sm text-slate-500">{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>

        <button
          onClick={onClose}
          className="mt-5 w-full rounded-xl bg-[#0f766e] py-3.5 font-bold text-white transition-colors hover:bg-[#0b5f58] active:bg-[#094f49]"
        >
          Got it
        </button>
      </div>

      <style>{`
        @keyframes pwa-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes pwa-slide-up { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        .animate-pwa-fade { animation: pwa-fade 0.2s ease-out; }
        .animate-pwa-slide-up { animation: pwa-slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>
    </div>
  );
};

export default PWAInstallInstructions;
